import type * as React from "react";
import { Box } from "@mui/material";
import { SxProps } from "@mui/system";
import { alpha } from "@mui/material/styles";
import tagIconBolt from "assets/images/tag-icons-bolt.svg";
import tagIconRows from "assets/images/tag-icons-rows.svg";
import tagIconThreeQuartersAlt from "assets/images/tag-icons-three-querters-alt.svg";
import tagIconThumbsUp from "assets/images/tag-icons-thumbs-up.svg";
import tagIconWarning from "assets/images/tag-icons-warning.svg";
import { COLORS } from "components/v3/Theme/colors";
import Translate from "components/v3/sharedComponents/Translate";

export type GlContentTagVariant =
  | "all"
  | "new"
  | "inProgress"
  | "completed"
  | "overdue"
  | "important";

type GlContentTagStyle = {
  backgroundColor: string;
  borderColor: string;
  color: string;
  hoverBackgroundColor: string;
  hoverBorderColor: string;
  hoverColor: string;
};

type GlContentTagConfig = {
  defaultLabel: string;
  iconSrc: string;
  selected: GlContentTagStyle;
};

const GL_CONTENT_TAG_BORDER_RADIUS = "4px";

const GL_CONTENT_TAG_CONFIG: Record<GlContentTagVariant, GlContentTagConfig> = {
  all: {
    defaultLabel: "All",
    iconSrc: tagIconRows,
    selected: {
      backgroundColor: COLORS.PRIMARY,
      borderColor: COLORS.PRIMARY,
      color: COLORS.WHITE,
      hoverBackgroundColor: COLORS.WHITE,
      hoverBorderColor: COLORS.PRIMARY,
      hoverColor: COLORS.PRIMARY
    }
  },
  new: {
    defaultLabel: "New",
    iconSrc: tagIconBolt,
    selected: {
      backgroundColor: COLORS.ACCENT3_LIGHT,
      borderColor: COLORS.ACCENT3_LIGHT,
      color: COLORS.ACCENT3_DARKER,
      hoverBackgroundColor: COLORS.WHITE,
      hoverBorderColor: COLORS.ACCENT3,
      hoverColor: COLORS.ACCENT3_DARKER
    }
  },
  inProgress: {
    defaultLabel: "In progress",
    iconSrc: tagIconThreeQuartersAlt,
    selected: {
      backgroundColor: COLORS.ACCENT1_LIGHTEST,
      borderColor: COLORS.ACCENT1_LIGHTEST,
      color: COLORS.ACCENT1_DARKER,
      hoverBackgroundColor: COLORS.WHITE,
      hoverBorderColor: COLORS.ACCENT1,
      hoverColor: COLORS.ACCENT1_DARKER
    }
  },
  completed: {
    defaultLabel: "Completed",
    iconSrc: tagIconThumbsUp,
    selected: {
      backgroundColor: COLORS.ACCENT4_LIGHTEST,
      borderColor: COLORS.ACCENT4_LIGHTEST,
      color: COLORS.ACCENT4_DARKER,
      hoverBackgroundColor: COLORS.WHITE,
      hoverBorderColor: COLORS.ACCENT4,
      hoverColor: COLORS.ACCENT4_DARKER
    }
  },
  overdue: {
    defaultLabel: "Overdue",
    iconSrc: tagIconWarning,
    selected: {
      backgroundColor: alpha(COLORS.NOTICE, 0.12),
      borderColor: alpha(COLORS.NOTICE, 0.12),
      color: COLORS.NOTICE,
      hoverBackgroundColor: COLORS.WHITE,
      hoverBorderColor: COLORS.NOTICE,
      hoverColor: COLORS.NOTICE
    }
  },
  important: {
    defaultLabel: "Important",
    iconSrc: tagIconWarning,
    selected: {
      backgroundColor: COLORS.ACCENT4_LIGHTEST,
      borderColor: COLORS.ACCENT4_LIGHTEST,
      color: COLORS.ACCENT4,
      hoverBackgroundColor: COLORS.WHITE,
      hoverBorderColor: COLORS.ACCENT4,
      hoverColor: COLORS.ACCENT4
    }
  }
};

export type GlContentTagProps = {
  variant: GlContentTagVariant;
  className?: string;
  interactive?: boolean;
  label?: string;
  onClick?: () => void;
  selected?: boolean;
  sx?: SxProps;
};

const GlContentTag: React.FC<GlContentTagProps> = ({
  variant,
  className,
  interactive = false,
  label,
  onClick,
  selected = true,
  sx
}) => {
  const config = GL_CONTENT_TAG_CONFIG[variant];
  const style = config.selected;
  const isFilled = !interactive || selected;
  const displayLabel = label ?? config.defaultLabel;

  return (
    <Box
      className={["gl-content-tag", className].filter(Boolean).join(" ")}
      component={interactive ? "button" : "span"}
      onClick={onClick}
      type={interactive ? "button" : undefined}
      sx={{
        alignItems: "center",
        backgroundColor: isFilled
          ? style.backgroundColor
          : style.hoverBackgroundColor,
        border: `1px solid ${
          isFilled ? style.borderColor : style.hoverBorderColor
        }`,
        borderRadius: GL_CONTENT_TAG_BORDER_RADIUS,
        color: isFilled ? style.color : style.hoverColor,
        cursor: interactive ? "pointer" : "default",
        display: "inline-flex",
        font: "inherit",
        gap: "6px",
        lineHeight: 1,
        marginTop: "-3px",
        px: 0.75,
        py: 0.25,
        textTransform: "uppercase",
        transition:
          "background-color 0.2s ease, border-color 0.2s ease, color 0.2s ease",
        verticalAlign: "middle",
        ...(interactive && {
          "&:hover": {
            backgroundColor: style.hoverBackgroundColor,
            borderColor: style.hoverBorderColor,
            color: style.hoverColor
          }
        }),
        ...sx
      }}
    >
      <Box
        alt=""
        aria-hidden
        component="img"
        src={config.iconSrc}
        sx={{
          display: "block",
          flexShrink: 0,
          height: 14,
          width: 14
        }}
      />
      <Box
        component="span"
        sx={{
          fontSize: "12px",
          fontWeight: 600
        }}
      >
        <Translate text={displayLabel} />
      </Box>
    </Box>
  );
};

export default GlContentTag;
